// src/components/admin/mail/MailConfirmDialog.tsx
import React from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { Button, Modal, ModalBody, ModalFooter, ModalOverlay } from './mail.styles';

const Title = styled.h3`
    margin: 0 0 0.6rem 0;
    font-size: 1.15rem;
    font-weight: 700;
    color: #1e293b;
`;

const Message = styled.p`
    margin: 0;
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.55;
`;

interface Props {
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel?: string;
    danger?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Replaces the native `confirm()` the rest of the panel uses.
 *
 * Not cosmetic: Safari's "prevent this page from creating additional dialogs" checkbox can
 * suppress `confirm()` entirely, after which a delete either silently does nothing or
 * silently proceeds depending on the browser. Non-deterministic destructive actions are not
 * acceptable on a mailbox.
 */
const MailConfirmDialog: React.FC<Props> = ({
    open,
    title,
    message,
    confirmLabel,
    cancelLabel = 'Zrušiť',
    danger,
    onConfirm,
    onCancel,
}) => (
    <AnimatePresence>
        {open && (
            <ModalOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onCancel}
            >
                <Modal
                    style={{ maxWidth: 460 }}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                    <ModalBody>
                        <Title>{title}</Title>
                        <Message>{message}</Message>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" $variant="secondary" onClick={onCancel} whileTap={{ scale: 0.97 }}>
                            {cancelLabel}
                        </Button>
                        <Button
                            type="button"
                            $variant={danger ? 'danger' : 'primary'}
                            onClick={onConfirm}
                            whileTap={{ scale: 0.97 }}
                        >
                            {confirmLabel}
                        </Button>
                    </ModalFooter>
                </Modal>
            </ModalOverlay>
        )}
    </AnimatePresence>
);

export default MailConfirmDialog;
